import map from 'lodash/map'
import toPairs from 'lodash/toPairs'
import groupBy from 'lodash/groupBy'
import flatten from 'lodash/flatten'
import reduce from 'lodash/reduce'
import {exhaustive} from '@freckle/exhaustive'
import {ajaxCall} from '@freckle/ajax'
import {
  type ParserT,
  Parser,
  record,
  nullable,
  array,
  oneOf,
  string,
  number,
  literal,
  firstOf
} from '@freckle/parser'
import {
  type NonEmptyArray,
  mkNonEmpty,
  mapOnNonEmpty,
  headOnNonEmpty,
  nonEmptyToArray
} from '@freckle/non-empty'
import {mapMaybes} from '@freckle/maybe'

import {type RlDomainT, parseDomainAttrs} from '@frontrow/console/src/entities/models/rl-domain'
import {
  type RlStandardT,
  parseStandardAttrs
} from '@frontrow/console/src/entities/models/rl-standard'

const _allArtifactTags = [
  'sync_fast_bridge_errors',
  'sync_fast_bridge_math_errors',
  'sync_fast_bridge_math_placements',
  'sync_fast_bridge_ela_placements',
  'sync_ngmt_errors',
  'sync_ngmt_placements',
  'sync_nwea_map_math_placements',
  'sync_nwea_map_ela_placements',
  'sync_m_a_p_errors',
  'sync_m_a_p_math_placements',
  'sync_m_a_p_ela_placements',
  'sync_skipped_students'
] as const

export const allArtifactTags = [..._allArtifactTags]

type ArtifactTag = (typeof _allArtifactTags)[number]

const artifactTagParser = oneOf('ArtifactTagParser', allArtifactTags) as ParserT<ArtifactTag>

export type Artifact = {
  tag: ArtifactTag
  url: string
}

export const artifactParser: ParserT<Artifact> = record({
  tag: artifactTagParser,
  url: string()
})

export async function fetchArtifactData(artifact: Artifact): Promise<ArtifactData> {
  const resp = await ajaxCall({
    url: artifact.url,
    method: 'GET',
    dataType: 'json'
  })
  return Parser.run(resp, artifactDataParser)
}

type ArtifactData =
  | {
      artifactTag: 'sync_fast_bridge_errors'
      data: Array<SyncFastBridgeErrorArtifact>
    }
  | {
      artifactTag: 'sync_fast_bridge_math_errors'
      data: Array<SyncFastBridgeErrorArtifact>
    }
  | {
      artifactTag: 'sync_fast_bridge_math_placements'
      data: Array<SyncMathPlacementArtifact>
    }
  | {
      artifactTag: 'sync_fast_bridge_ela_placements'
      data: Array<SyncElaPlacementArtifact>
    }
  | {
      artifactTag: 'sync_ngmt_errors'
      data: Array<SyncNgmtErrorArtifact>
    }
  | {
      artifactTag: 'sync_ngmt_placements'
      data: Array<SyncMathPlacementArtifact>
    }
  | {
      artifactTag: 'sync_nwea_map_math_placements'
      data: Array<SyncMathPlacementArtifact>
    }
  | {
      artifactTag: 'sync_nwea_map_ela_placements'
      data: Array<SyncElaPlacementArtifact>
    }
  | {
      artifactTag: 'sync_m_a_p_errors'
      data: Array<SyncMAPErrorArtifact>
    }
  | {
      artifactTag: 'sync_m_a_p_math_placements'
      data: Array<SyncMathPlacementArtifact>
    }
  | {
      artifactTag: 'sync_m_a_p_ela_placements'
      data: Array<SyncElaPlacementArtifact>
    }
  | {
      artifactTag: 'sync_skipped_students'
      data: Array<SyncSkippedStudentArtifact>
    }

type SyncMathPlacementArtifact = {
  studentName: string
  studentSisId: string
  schoolName: string | undefined | null
  rlDomain: RlDomainT | undefined | null
  rlStandard: RlStandardT | undefined | null
}

const syncMathPlacementArtifactParser: ParserT<SyncMathPlacementArtifact> = record({
  studentName: string(),
  studentSisId: string(),
  schoolName: nullable(string()),
  rlDomain: nullable(parseDomainAttrs),
  rlStandard: nullable(parseStandardAttrs)
})

type SyncElaPlacementArtifact = {
  studentId: number
  sisId: string
  readingLevel: string
}

const syncElaPlacementArtifactParser: ParserT<SyncElaPlacementArtifact> = record({
  studentId: number(),
  sisId: string(),
  readingLevel: string()
})

type InvalidStudentDetails = {
  firstName: string
  lastName: string
  grade: number
}

const invalidStudentDetailsParser: ParserT<InvalidStudentDetails> = record({
  firstName: string(),
  lastName: string(),
  grade: number()
})

type InvalidStarScoreDetails = {
  score: number
}

const invalidStarScoreDetailsParser: ParserT<InvalidStarScoreDetails> = record({
  score: number()
})

type InvalidStudentPrevPlacedDetails = {
  studentId: number
}

const invalidStudentPrevPlacedDetailsParser: ParserT<InvalidStudentPrevPlacedDetails> = record({
  studentId: number()
})

type InvalidNoTranslationDetails = {
  fast_bridge_score: number
  studentId: number
}

const invalidNoTranslationDetailsParser: ParserT<InvalidNoTranslationDetails> = record({
  fast_bridge_score: number(),
  studentId: number()
})

type SyncSkippedStudentArtifact = {
  sisId: string | undefined | null
  studentId: number
  reason: string
}

const syncSkippedStudentArtifactParser: ParserT<SyncSkippedStudentArtifact> = record({
  sisId: nullable(string()),
  studentId: number(),
  reason: string()
})

type ErrorWithDetails<name, details> = {
  name: name
  message: string
  details: details
}

type SyncFastBridgeErrorArtifact =
  | ErrorWithDetails<'math_star_placement_invalid_student', InvalidStudentDetails>
  | ErrorWithDetails<'math_star_placement_invalid_star_score', InvalidStarScoreDetails>
  | ErrorWithDetails<
      'math_star_placement_student_was_previously_placed',
      InvalidStudentPrevPlacedDetails
    >
  | ErrorWithDetails<
      'math_star_placement_no_translation_for_fast_bridge',
      InvalidNoTranslationDetails
    >

type SyncMAPErrorArtifact =
  | ErrorWithDetails<'math_star_placement_invalid_student', InvalidStudentDetails>
  | ErrorWithDetails<'math_star_placement_invalid_star_score', InvalidStarScoreDetails>
  | ErrorWithDetails<
      'math_star_placement_student_was_previously_placed',
      InvalidStudentPrevPlacedDetails
    >

type InvalidNoNgmtTranslationDetails = {
  ngmt_score: number
  studentId: number
}

const invalidNoNgmtTranslationDetailsParser: ParserT<InvalidNoNgmtTranslationDetails> = record({
  ngmt_score: number(),
  studentId: number()
})
type SyncNgmtErrorArtifact =
  | ErrorWithDetails<'math_star_placement_invalid_student', InvalidStudentDetails>
  | ErrorWithDetails<'math_star_placement_invalid_star_score', InvalidStarScoreDetails>
  | ErrorWithDetails<
      'math_star_placement_student_was_previously_placed',
      InvalidStudentPrevPlacedDetails
    >
  | ErrorWithDetails<'no_translation_for_ngmt', InvalidNoNgmtTranslationDetails>

const syncFastBridgeMathErrorArtifactParser: ParserT<SyncFastBridgeErrorArtifact> = firstOf(
  record({
    name: literal('math_star_placement_invalid_student'),
    message: string(),
    details: invalidStudentDetailsParser
  }),
  record({
    name: literal('math_star_placement_invalid_star_score'),
    message: string(),
    details: invalidStarScoreDetailsParser
  }),
  record({
    name: literal('math_star_placement_student_was_previously_placed'),
    message: string(),
    details: invalidStudentPrevPlacedDetailsParser
  }),
  record({
    name: literal('math_star_placement_no_translation_for_fast_bridge'),
    message: string(),
    details: invalidNoTranslationDetailsParser
  })
)

const syncNgmtErrorArtifactParser = firstOf(
  record({
    name: literal('math_star_placement_invalid_student'),
    message: string(),
    details: invalidStudentDetailsParser
  }),
  record({
    name: literal('math_star_placement_invalid_star_score'),
    message: string(),
    details: invalidStarScoreDetailsParser
  }),
  record({
    name: literal('math_star_placement_student_was_previously_placed'),
    message: string(),
    details: invalidStudentPrevPlacedDetailsParser
  }),
  record({
    name: literal('no_translation_for_ngmt'),
    message: string(),
    details: invalidNoNgmtTranslationDetailsParser
  })
)

const syncMAPErrorArtifactParser: ParserT<SyncMAPErrorArtifact> = firstOf(
  record({
    name: literal('math_star_placement_invalid_student'),
    message: string(),
    details: invalidStudentDetailsParser
  }),
  record({
    name: literal('math_star_placement_invalid_star_score'),
    message: string(),
    details: invalidStarScoreDetailsParser
  }),
  record({
    name: literal('math_star_placement_student_was_previously_placed'),
    message: string(),
    details: invalidStudentPrevPlacedDetailsParser
  })
)

const artifactDataParser: ParserT<ArtifactData> = firstOf(
  record({
    artifactTag: literal('sync_fast_bridge_errors'),
    data: array(syncFastBridgeMathErrorArtifactParser)
  }),
  record({
    artifactTag: literal('sync_fast_bridge_math_errors'),
    data: array(syncFastBridgeMathErrorArtifactParser)
  }),
  record({
    artifactTag: literal('sync_fast_bridge_math_placements'),
    data: array(syncMathPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_fast_bridge_ela_placements'),
    data: array(syncElaPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_ngmt_errors'),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: array(syncNgmtErrorArtifactParser) as any
  }),
  record({
    artifactTag: literal('sync_ngmt_placements'),
    data: array(syncMathPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_nwea_map_math_placements'),
    data: array(syncMathPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_nwea_map_ela_placements'),
    data: array(syncElaPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_m_a_p_errors'),
    data: array(syncMAPErrorArtifactParser)
  }),
  record({
    artifactTag: literal('sync_m_a_p_math_placements'),
    data: array(syncMathPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_m_a_p_ela_placements'),
    data: array(syncElaPlacementArtifactParser)
  }),
  record({
    artifactTag: literal('sync_skipped_students'),
    data: array(syncSkippedStudentArtifactParser)
  })
)

// Type that ties together `ArtifactTag` and a specifc `ArtifactData`.
export type GroupedArtifactData = {
  sync_fast_bridge_errors: Array<SyncFastBridgeErrorArtifact>
  sync_fast_bridge_math_placements: Array<SyncMathPlacementArtifact>
  sync_fast_bridge_ela_placements: Array<SyncElaPlacementArtifact>
  sync_ngmt_errors: Array<SyncNgmtErrorArtifact>
  sync_ngmt_placements: Array<SyncMathPlacementArtifact>
  sync_nwea_map_math_placements: Array<SyncMathPlacementArtifact>
  sync_nwea_map_ela_placements: Array<SyncElaPlacementArtifact>
  sync_m_a_p_errors: Array<SyncMAPErrorArtifact>
  sync_m_a_p_math_placements: Array<SyncMathPlacementArtifact>
  sync_m_a_p_ela_placements: Array<SyncElaPlacementArtifact>
  sync_skipped_students: Array<SyncSkippedStudentArtifact>
}

const emptyGroupedArtifactData: GroupedArtifactData = {
  sync_fast_bridge_errors: [],
  sync_fast_bridge_math_placements: [],
  sync_fast_bridge_ela_placements: [],
  sync_ngmt_errors: [],
  sync_ngmt_placements: [],
  sync_nwea_map_math_placements: [],
  sync_nwea_map_ela_placements: [],
  sync_m_a_p_errors: [],
  sync_m_a_p_math_placements: [],
  sync_m_a_p_ela_placements: [],
  sync_skipped_students: []
}

export function groupArtifactData(artifactData: Array<ArtifactData>): GroupedArtifactData {
  const pairs: Array<[string, Array<ArtifactData>]> = toPairs(
    groupBy(artifactData, ({artifactTag}) => artifactTag)
  )

  const groupedByTag: NonEmptyArray<NonEmptyArray<ArtifactData>> | undefined | null = mkNonEmpty(
    mapMaybes(
      map(pairs, ([_, artifactDatas]) => artifactDatas),
      mkNonEmpty
    )
  )

  if (groupedByTag === null || groupedByTag === undefined) return emptyGroupedArtifactData

  const groupedArtifactDatas = mapOnNonEmpty(
    groupedByTag,
    (artifactDatas: NonEmptyArray<ArtifactData>) => {
      const emptyGroup = {
        sync_fast_bridge_errors: [],
        sync_fast_bridge_math_placements: [],
        sync_fast_bridge_ela_placements: [],
        sync_ngmt_errors: [],
        sync_ngmt_placements: [],
        sync_nwea_map_math_placements: [],
        sync_nwea_map_ela_placements: [],
        sync_m_a_p_errors: [],
        sync_m_a_p_math_placements: [],
        sync_m_a_p_ela_placements: [],
        sync_skipped_students: []
      }

      const {artifactTag} = headOnNonEmpty(artifactDatas)
      switch (artifactTag) {
        case 'sync_fast_bridge_errors':
        case 'sync_fast_bridge_math_errors':
          const errors: Array<SyncFastBridgeErrorArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                  return data.data
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          {
            return {
              ...emptyGroup,
              sync_fast_bridge_errors: errors
            }
          }

        case 'sync_fast_bridge_math_placements':
          const mathPlacementData: Array<SyncMathPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_fast_bridge_math_placements':
                  return data.data
                case 'sync_fast_bridge_ela_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          {
            return {
              ...emptyGroup,
              sync_fast_bridge_math_placements: mathPlacementData
            }
          }

        case 'sync_fast_bridge_ela_placements':
          const elaPlacementData: Array<SyncElaPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_fast_bridge_ela_placements':
                  return data.data
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          {
            return {
              ...emptyGroup,
              sync_fast_bridge_ela_placements: elaPlacementData
            }
          }
        case 'sync_ngmt_errors':
          const sync_ngmt_errors: Array<SyncNgmtErrorArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_ngmt_errors':
                  return data.data
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          {
            return {...emptyGroup, sync_ngmt_errors}
          }

        case 'sync_ngmt_placements':
          const sync_ngmt_placements: Array<SyncMathPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_ngmt_placements':
                  return data.data
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          {
            return {...emptyGroup, sync_ngmt_placements}
          }

        case 'sync_nwea_map_math_placements': {
          const mathPlacementData: Array<SyncMathPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_nwea_map_math_placements':
                  return data.data
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          return {
            ...emptyGroup,
            sync_nwea_map_math_placements: mathPlacementData
          }
        }
        case 'sync_nwea_map_ela_placements': {
          const elaPlacementData: Array<SyncElaPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_nwea_map_ela_placements':
                  return data.data
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )

          {
            return {
              ...emptyGroup,
              sync_nwea_map_ela_placements: elaPlacementData
            }
          }
        }

        case 'sync_m_a_p_errors': {
          const errors: Array<SyncMAPErrorArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_m_a_p_errors':
                  return data.data
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )
          {
            return {
              ...emptyGroup,
              sync_m_a_p_errors: errors
            }
          }
        }
        case 'sync_m_a_p_math_placements': {
          const mathPlacementData: Array<SyncMathPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_m_a_p_math_placements':
                  return data.data
                case 'sync_m_a_p_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )

          {
            return {
              ...emptyGroup,
              sync_m_a_p_math_placements: mathPlacementData
            }
          }
        }
        case 'sync_m_a_p_ela_placements': {
          const elaPlacementData: Array<SyncElaPlacementArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_m_a_p_ela_placements':
                  return data.data
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_errors':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_skipped_students':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )

          {
            return {
              ...emptyGroup,
              sync_m_a_p_ela_placements: elaPlacementData
            }
          }
        }
        case 'sync_skipped_students': {
          const skipped: Array<SyncSkippedStudentArtifact> = flatten(
            mapMaybes(nonEmptyToArray(artifactDatas), data => {
              switch (data.artifactTag) {
                case 'sync_skipped_students':
                  return data.data
                case 'sync_fast_bridge_errors':
                case 'sync_fast_bridge_math_errors':
                case 'sync_fast_bridge_math_placements':
                case 'sync_fast_bridge_ela_placements':
                case 'sync_ngmt_errors':
                case 'sync_ngmt_placements':
                case 'sync_nwea_map_math_placements':
                case 'sync_nwea_map_ela_placements':
                case 'sync_m_a_p_errors':
                case 'sync_m_a_p_math_placements':
                case 'sync_m_a_p_ela_placements':
                  return null
                default:
                  return exhaustive(data)
              }
            })
          )

          return {...emptyGroup, sync_skipped_students: skipped}
        }
        default:
          return exhaustive(artifactTag)
      }
    }
  )

  const append = (g1: GroupedArtifactData, g2: GroupedArtifactData): GroupedArtifactData => ({
    sync_fast_bridge_errors: [...g1.sync_fast_bridge_errors, ...g2.sync_fast_bridge_errors],

    sync_fast_bridge_math_placements: [
      ...g1.sync_fast_bridge_math_placements,
      ...g2.sync_fast_bridge_math_placements
    ],

    sync_fast_bridge_ela_placements: [
      ...g1.sync_fast_bridge_ela_placements,
      ...g2.sync_fast_bridge_ela_placements
    ],

    sync_ngmt_errors: [...g1.sync_ngmt_errors, ...g2.sync_ngmt_errors],
    sync_ngmt_placements: [...g1.sync_ngmt_placements, ...g2.sync_ngmt_placements],

    sync_nwea_map_math_placements: [
      ...g1.sync_nwea_map_math_placements,
      ...g2.sync_nwea_map_math_placements
    ],

    sync_nwea_map_ela_placements: [
      ...g1.sync_nwea_map_ela_placements,
      ...g2.sync_nwea_map_ela_placements
    ],

    sync_m_a_p_errors: [...g1.sync_m_a_p_errors, ...g2.sync_m_a_p_errors],
    sync_m_a_p_math_placements: [
      ...g1.sync_m_a_p_math_placements,
      ...g2.sync_m_a_p_math_placements
    ],

    sync_m_a_p_ela_placements: [...g1.sync_m_a_p_ela_placements, ...g2.sync_m_a_p_ela_placements],

    sync_skipped_students: [...g1.sync_skipped_students, ...g2.sync_skipped_students]
  })

  return reduce(groupedArtifactDatas, append, emptyGroupedArtifactData)
}
